import {
  Compass,
  Camera,
  Users,
  Store,
  CreditCard,
  Settings,
  LifeBuoy,
} from '@/lib/icons';

// ── Primitives ───────────────────────────────────────────────────────────

const H2 = ({ children }) => (
  <h2 className="text-2xl font-semibold mt-10 mb-3 text-foreground">{children}</h2>
);
const H3 = ({ children }) => (
  <h3 className="text-lg font-semibold mt-6 mb-2 text-foreground">{children}</h3>
);
const P = ({ children }) => (
  <p className="text-base leading-relaxed text-muted-foreground mb-4">{children}</p>
);
const Ul = ({ children }) => (
  <ul className="list-disc pl-6 space-y-2 mb-4 text-muted-foreground">{children}</ul>
);
const Li = ({ children }) => <li className="leading-relaxed">{children}</li>;
const Code = ({ children }) => (
  <code className="px-1.5 py-0.5 rounded bg-secondary text-foreground text-sm font-mono">{children}</code>
);
const Note = ({ children }) => (
  <div className="my-4 rounded-xl border border-border bg-secondary/40 p-4 text-sm text-muted-foreground">
    {children}
  </div>
);

// ── Categories (drives the sidebar) ──────────────────────────────────────

export const CATEGORIES = [
  {
    id: 'getting-started',
    label: 'Getting Started',
    icon: Compass,
    topics: ['welcome', 'sign-up', 'claim-username', 'profile'],
  },
  {
    id: 'sharing',
    label: 'Sharing & Posting',
    icon: Camera,
    topics: ['posts', 'revs', 'stories', 'garage'],
  },
  {
    id: 'community',
    label: 'Community',
    icon: Users,
    topics: ['channels', 'clubs', 'events', 'tracks', 'messages', 'discovery', 'collections'],
  },
  {
    id: 'marketplace',
    label: 'Marketplace & Money',
    icon: Store,
    topics: ['marketplace', 'shop', 'dealers', 'creator-monetization', 'payments'],
  },
  {
    id: 'account',
    label: 'Account & Tools',
    icon: Settings,
    topics: ['notifications', 'leaderboard', 'livestreams', 'settings', 'safety'],
  },
  {
    id: 'about',
    label: 'About OCTNE',
    icon: LifeBuoy,
    topics: ['contact', 'legal'],
  },
];

// ── Topics ───────────────────────────────────────────────────────────────

export const TOPICS = {
  // ── Getting Started ──────────────────────────────────────────────────
  welcome: {
    title: 'Welcome to OCTNE',
    description: 'A quick orientation to the platform and what you can do here.',
    body: (
      <>
        <P>
          OCTNE is a social platform built for car enthusiasts, builders, sellers, and creators.
          You can share your build, browse vehicles and parts, follow channels, join clubs,
          attend events, and connect with other people who share your interest in cars.
        </P>
        <P>
          This help center is split into six categories in the sidebar: Getting Started,
          Sharing &amp; Posting, Community, Marketplace &amp; Money, Account &amp; Tools, and
          About OCTNE. Each category contains topics that explain a specific feature in
          plain language with the steps you need.
        </P>
        <H2>What you can do on OCTNE</H2>
        <Ul>
          <Li>Build a public garage of your cars with photos, mod lists, and build stages.</Li>
          <Li>Post photos, videos, build updates, before/after comparisons, polls, and short text posts.</Li>
          <Li>Watch and upload long-form video to Channels, or scroll Revs (vertical short-form video).</Li>
          <Li>Buy and sell parts and vehicles in the Marketplace, or open a Shop storefront.</Li>
          <Li>List a dealership, manage leads, reviews, and inventory under Dealer Tools.</Li>
          <Li>Run or attend events, lap tracks, and join clubs around your interests.</Li>
        </Ul>
        <H2>Where to start</H2>
        <P>
          If you&apos;re new, read <strong>Sign Up &amp; Onboarding</strong> next. If you&apos;ve already created an
          account, jump straight to <strong>Your Profile</strong> to add an avatar, bio, and your first car.
        </P>
        <Note>
          OCTNE is in active development. Some areas (creator monetization, payouts, dealer billing)
          are progressively rolling out. If something looks empty in your account, the feature may
          not be enabled for your role yet — see <strong>Contact &amp; Support</strong>.
        </Note>
      </>
    ),
  },

  'sign-up': {
    title: 'Sign Up & Onboarding',
    description: 'Create an account, pick a username, and finish onboarding.',
    body: (
      <>
        <P>
          Sign up with email and a password. After your email is confirmed, OCTNE walks you
          through a short onboarding flow at <Code>/onboarding</Code>. You&apos;ll choose a username,
          select your car interests, optionally provide a birth date and gender, and see suggested
          builders to follow.
        </P>
        <H2>Picking a username</H2>
        <P>
          Usernames are lowercase, can contain letters, numbers, and underscores, and have to be
          unique across OCTNE. If your first choice is taken, OCTNE suggests close alternatives
          you can pick with one tap. You can change your username later in Settings, but every
          change updates the URL of your profile and may break older shared links.
        </P>
        <H2>Choosing interests</H2>
        <P>
          You can select one or more style categories — JDM, Euro, Muscle, Off-Road, Truck,
          Exotic, Classic, or Other. These selections seed your personalized feed. You can change
          them at any time from Settings, or your feed will adapt over time as you like, save, and
          follow more content.
        </P>
        <H2>Suggested builders</H2>
        <P>
          The last onboarding step suggests several creators and builders you might want to
          follow. You can follow any of them with one tap, skip the step entirely, or come back
          later from <strong>Discover</strong> or <strong>Search</strong>.
        </P>
        <H2>Referral codes</H2>
        <P>
          If you arrived via a referral link, the referral code is captured during onboarding and
          credited to the referrer. Referral programs and any associated rewards are surfaced
          inside the Creator Suite for eligible creators.
        </P>
      </>
    ),
  },

  'claim-username': {
    title: 'Claiming a Reserved Username',
    description: 'How brands and creators verify and claim a reserved handle.',
    body: (
      <>
        <P>
          OCTNE reserves usernames for known brands, manufacturers, and notable creators so that
          impersonators can&apos;t register them. If your business or persona has a reserved
          username, you can claim it at <Code>/claim-username</Code>.
        </P>
        <H2>How the claim flow works</H2>
        <Ul>
          <Li>
            Enter the reserved username (for example, <Code>ferrari</Code>) and OCTNE looks it up.
          </Li>
          <Li>
            If you sign in with an email on the brand&apos;s allowlist (specific addresses or an
            allowed domain like <Code>@brand.com</Code>), the username is auto-claimed and a
            verified badge is added to your account.
          </Li>
          <Li>
            If your email isn&apos;t auto-approved, the request is sent to OCTNE for manual review.
            You&apos;ll be notified when the team reaches a decision.
          </Li>
        </Ul>
        <H2>If your username isn&apos;t reserved</H2>
        <P>
          If the username you searched for isn&apos;t in the reserved list, the lookup will tell
          you. You can either choose a different username during sign-up or contact OCTNE if you
          believe a reservation should exist for your brand.
        </P>
        <H2>Already claimed</H2>
        <P>
          If a reserved username has already been claimed, the lookup will say so. If you believe
          a username was claimed by the wrong account, contact <Code>legal@octne.com</Code> with
          documentation of your trademark or rights.
        </P>
        <Note>
          Verified badges are only granted at claim time for auto-approved domains, or as part of
          a manual review. Verification is not for sale and isn&apos;t available through Settings.
        </Note>
      </>
    ),
  },

  profile: {
    title: 'Your Profile',
    description: 'Edit your profile, avatar, banner, and showcase your work.',
    body: (
      <>
        <P>
          Your profile at <Code>/profile</Code> is where you appear to other users. It shows your
          avatar, banner, bio, location, links, post grid, garage, saved items, reposts, and any
          highlights you&apos;ve pinned.
        </P>
        <H2>Editing the basics</H2>
        <P>
          Tap <strong>Edit Profile</strong> to update your avatar, banner image, display name,
          username, bio, location, and external links. Your bio supports plain text — keep it
          short and skimmable. Your banner is shown at the top of your profile and on shared
          profile previews.
        </P>
        <H2>Profile tabs</H2>
        <Ul>
          <Li><strong>Posts</strong> — every post you&apos;ve published, in reverse chronological order.</Li>
          <Li><strong>Revs</strong> — your vertical-video posts, played full-screen.</Li>
          <Li><strong>Cars</strong> — your garage, with each car&apos;s mod count and likes.</Li>
          <Li><strong>Saves</strong> — posts you&apos;ve bookmarked. Only visible to you.</Li>
          <Li><strong>Reposts</strong> — posts you&apos;ve reposted into your own feed.</Li>
        </Ul>
        <H2>Highlights</H2>
        <P>
          Highlights are curated story collections that stay on your profile after the original
          24-hour story window. Use them to keep build progress, event coverage, or signature
          builds permanently visible.
        </P>
        <H2>Sharing your profile</H2>
        <P>
          Every profile has a shareable URL of the form <Code>/user/your-username</Code>, and a
          QR code in the profile menu that you can show in person or print on a sticker.
        </P>
      </>
    ),
  },

  // ── Sharing & Posting ────────────────────────────────────────────────
  posts: {
    title: 'Posts & Feed',
    description: 'Write posts, schedule them, and use the For You / Following feeds.',
    body: (
      <>
        <P>
          The home feed at <Code>/</Code> has two tabs — <strong>For You</strong> (algorithmic,
          based on what you like and follow) and <strong>Following</strong> (chronological, from
          accounts you follow). Posts can include photos, videos, captions, hashtags, mentions,
          and polls.
        </P>
        <H2>Post types</H2>
        <Ul>
          <Li><strong>Photo</strong> — single image or multi-image carousel.</Li>
          <Li><strong>Build update</strong> — multi-image post tied to a car in your garage and a build stage.</Li>
          <Li><strong>Before / after</strong> — two images shown side-by-side.</Li>
          <Li><strong>Text</strong> — short post with no media.</Li>
          <Li><strong>Poll</strong> — question with two or more options other users can vote on.</Li>
        </Ul>
        <H2>Captions, hashtags, and mentions</H2>
        <P>
          Captions support up to 2,200 characters. Add hashtags by pressing Enter after each tag
          (no spaces). Hashtags become tappable links that open <Code>/discover?tag=...</Code> with
          every post using that tag. To mention another user, type <Code>@</Code> and pick from the
          autocomplete list — they&apos;ll get a tag notification when the post publishes.
        </P>
        <H2>Linking a car</H2>
        <P>
          You can attach a post to one of the cars in your garage. The post then shows the year,
          make, and model badge, and any clicks on that badge take other users to your car&apos;s
          page. If the post is a build update, you can also pick a <strong>build stage</strong>
          (Stock, Stage&nbsp;1, Stage&nbsp;2, Stage&nbsp;3, or Full Build).
        </P>
        <H2>Drafts and scheduling</H2>
        <P>
          Every post can be saved as a draft (visible only to you), scheduled for a future date,
          or published immediately. Manage your scheduled and draft posts from your{' '}
          <strong>Content Calendar</strong>. Scheduled posts publish automatically at the chosen
          time — you don&apos;t need to be online.
        </P>
        <H2>After posting</H2>
        <P>
          You can pin one of your posts to the top of your profile, archive a post (it disappears
          from feeds but you keep the data), or delete it permanently. Likes, comments, saves,
          shares, and reposts on your posts feed into your <strong>Insights</strong>.
        </P>
        <Note>
          OCTNE runs lightweight image moderation on uploads. Anything flagged as unsafe is
          rejected with a notice — re-upload a different image.
        </Note>
      </>
    ),
  },

  revs: {
    title: 'Revs (Vertical Video)',
    description: 'Full-screen vertical video feed and how to post Revs.',
    body: (
      <>
        <P>
          <strong>Revs</strong> at <Code>/revs</Code> is a full-screen vertical video feed.
          Swipe up or down to move between clips. You can like, save, comment, repost, share, and
          follow the creator from the right-hand action rail. Tap the screen to pause and resume.
        </P>
        <H2>Audio</H2>
        <P>
          Revs play with the sound off by default. Tap the speaker icon to unmute — your choice
          is remembered as you scroll. Some creators credit a sound or music track on their clip,
          which is displayed in a small label at the bottom.
        </P>
        <H2>Posting a Rev</H2>
        <P>
          Any video post you publish appears in Revs as long as it&apos;s a portrait-orientation
          video. The same captions, hashtags, mentions, and car links you can attach to a regular
          post apply here. Use a 9:16 aspect ratio (1080×1920) for the cleanest playback.
        </P>
        <H2>Linked car badge</H2>
        <P>
          If you tag your Rev with a car from your garage, viewers see a year/make/model badge
          they can tap to jump to your car&apos;s page. This is a quick way to drive traffic to
          your build.
        </P>
        <H2>Watch history and analytics</H2>
        <P>
          OCTNE tracks how long viewers watch your Revs (anonymized in aggregate). You can see
          per-post watch metrics in <strong>Insights</strong> alongside likes and comments.
        </P>
      </>
    ),
  },

  stories: {
    title: 'Stories',
    description: '24-hour photos and clips with text, stickers, and filters.',
    body: (
      <>
        <P>
          Stories are short photo or video posts that stay on your profile and in your followers&apos;
          story ring for 24 hours. You can add filters, drag-and-drop text overlays, stickers, and
          a caption.
        </P>
        <H2>Creating a story</H2>
        <P>
          Tap your avatar in the story ring, or pick a story option from the post creator. Choose
          a photo or video from your library, or open the camera. After you pick media, the editor
          lets you tweak it before posting.
        </P>
        <H2>Text overlays</H2>
        <P>
          Tap the <strong>T</strong> tool to add a text caption anywhere on the story. Six themes
          are available — Classic, Bold, Neon, Fire, Outline, and Gradient — each with its own
          font weight and color treatment. Drag the text anywhere on the canvas; positions are
          stored as percentages so the text stays put across screen sizes.
        </P>
        <H2>Stickers</H2>
        <P>
          The <strong>Sticker</strong> tool opens a Klipy-powered picker where you can search for
          stickers and GIFs and drop them on the story. Like text, stickers are draggable and
          repositionable.
        </P>
        <H2>Filters</H2>
        <P>
          Seven photo filters are available — Normal, Vintage, Cold, Warm, B&amp;W, Fade, and
          Vivid. Filters are applied as CSS, so they&apos;re non-destructive — your original
          media is unchanged.
        </P>
        <H2>Highlights</H2>
        <P>
          To keep a story past 24 hours, save it to a <strong>Highlight</strong> on your profile.
          Highlights appear as circles above your post grid and stay until you delete them.
        </P>
      </>
    ),
  },

  garage: {
    title: 'Garage & Cars',
    description: 'Add and manage your cars, build stages, and mods.',
    body: (
      <>
        <P>
          Your <strong>Garage</strong> is where every car you own (or have owned) lives on OCTNE.
          From <Code>/rides</Code> you can add new cars, edit existing ones, and toggle each
          car&apos;s privacy.
        </P>
        <H2>Adding a car</H2>
        <P>
          Click <strong>Add Car</strong>. Fill in the year, make, model, color, mileage, and
          (optionally) a cover image. The car appears in your garage and becomes available to
          link to posts and Revs.
        </P>
        <H2>Build stages</H2>
        <Ul>
          <Li><strong>Stock</strong> — no modifications.</Li>
          <Li><strong>Stage 1</strong> — basic bolt-ons.</Li>
          <Li><strong>Stage 2</strong> — significant tuning, suspension, or aero work.</Li>
          <Li><strong>Stage 3</strong> — heavy modification, motor work, or extensive aero/exterior changes.</Li>
          <Li><strong>Full Build</strong> — major fabrication, motor swap, or chassis work.</Li>
        </Ul>
        <H2>Mods and style tags</H2>
        <P>
          Each car has a list of <strong>mod tags</strong> (free-form text — turbo, coilovers,
          forged wheels, wrap, etc.) and one or more <strong>style tags</strong> chosen from a
          fixed list (JDM, Euro, Muscle, Off-Road, Truck, Exotic, Classic, Other). Style tags
          power filters in Discover and on the leaderboard.
        </P>
        <H2>Public vs private cars</H2>
        <P>
          You can mark any car as private. Private cars only show up in your own garage and
          can&apos;t be linked from public posts. Cars that show up on public garage URLs like{' '}
          <Code>/garage/your-username</Code> only include public cars.
        </P>
        <H2>Cover image and gallery</H2>
        <P>
          The cover image is shown on the car card in your garage and on any post that links the
          car. Each car has its own dedicated page that shows every post you&apos;ve made about
          it, in chronological build order.
        </P>
      </>
    ),
  },

  // ── Community ────────────────────────────────────────────────────────
  channels: {
    title: 'Channels',
    description: 'Long-form video on OCTNE — uploading, premieres, and subscriptions.',
    body: (
      <>
        <P>
          <strong>Channels</strong> at <Code>/channels</Code> is the home of long-form video on
          OCTNE — build series, track day vlogs, tutorials, reviews, and event coverage. Channels
          are separate from Revs. You watch full videos at <Code>/watch/[videoId]</Code>.
        </P>
        <H2>Following and subscribing</H2>
        <P>
          You can <strong>follow</strong> any channel for free to see new uploads in your
          channels feed. Some channels also offer paid <strong>subscriptions</strong> — see{' '}
          <strong>Creator Monetization</strong> for how that works. Free follows and paid
          subscriptions are tracked separately.
        </P>
        <H2>Premieres</H2>
        <P>
          Creators can schedule a video as a <strong>premiere</strong>. Until the premiere time,
          the video page shows a countdown clock and a notify button. At the scheduled time, the
          video unlocks and starts playing live for everyone — useful for build reveals, race
          recaps, and first-look videos.
        </P>
        <H2>Uploading a video</H2>
        <P>
          From your channel dashboard, click <strong>Upload</strong>. You can set the title,
          description, thumbnail, tags, monetization status (if eligible), and either publish
          immediately or schedule a premiere.
        </P>
        <H2>Channel sections</H2>
        <Ul>
          <Li><strong>Continue Watching</strong> — videos you started but haven&apos;t finished.</Li>
          <Li><strong>Trending Videos</strong> — channel uploads currently picking up speed.</Li>
          <Li><strong>Since You Watched</strong> — recommendations based on recent views.</Li>
          <Li><strong>In Case You Missed</strong> — back-catalog from creators you follow.</Li>
        </Ul>
      </>
    ),
  },

  clubs: {
    title: 'Clubs',
    description: 'Communities around interests, regions, or specific cars.',
    body: (
      <>
        <P>
          <strong>Clubs</strong> at <Code>/clubs</Code> are sub-communities. A club can be public
          (anyone can join) or private (membership requires approval). Posts inside a club are
          visible only to club members and don&apos;t appear in the main feed.
        </P>
        <H2>Joining</H2>
        <P>
          Browse clubs in <strong>Discover</strong>, <strong>My Feed</strong>, and{' '}
          <strong>My Clubs</strong>. Tap <strong>Join</strong> to enter a public club or request
          access to a private one. Private clubs require an admin to approve your request — you
          can see your status as <strong>Pending</strong> until then.
        </P>
        <H2>Posting in a club</H2>
        <P>
          Club posts use the same composer as regular posts but are scoped to the club. Use
          them for member-only build updates, region-specific meet planning, or threads that
          would clutter the main feed. Club posts can be pinned by admins.
        </P>
        <H2>Announcements</H2>
        <P>
          Club admins can send <strong>announcements</strong> — high-visibility posts that pin to
          the top of the club feed and (optionally) email all members. Use announcements
          sparingly: they&apos;re intended for events, big news, or schedule changes.
        </P>
        <H2>Club events</H2>
        <P>
          Clubs can host events (meets, drives, track days). Members get RSVPs and a notification
          stream specific to the club. See <strong>Events &amp; Passport</strong> for how RSVPs
          and check-ins work.
        </P>
        <H2>Creating a club</H2>
        <P>
          Anyone can create a club — pick a name, slug, banner, region, and a one-line
          description. As the founder, you become the first admin and can invite or promote other
          members. Clubs you create show up under <strong>My Clubs</strong>.
        </P>
      </>
    ),
  },

  events: {
    title: 'Events & Passport',
    description: 'RSVPs, capacity limits, ticketing, and the Event Passport.',
    body: (
      <>
        <P>
          <strong>Events</strong> at <Code>/events</Code> covers car meets, track days, car shows,
          cruises, and auctions. Events have a date, a location, a capacity, an optional ticket
          price, and an organizer.
        </P>
        <H2>RSVPing</H2>
        <P>
          Tap <strong>RSVP</strong> to confirm your spot. If the event is at capacity, you can
          join the <strong>Waitlist</strong> — if a spot opens up, OCTNE notifies the next person
          in line. You can cancel an RSVP at any time before the event starts.
        </P>
        <H2>Ticketing</H2>
        <P>
          Free events confirm your RSVP instantly. Paid events charge through Stripe — see{' '}
          <strong>Payments &amp; Stripe</strong>. After payment, your ticket is added to your{' '}
          <strong>Event Passport</strong>.
        </P>
        <H2>Event Passport</H2>
        <P>
          Your Event Passport is a running record of every event you&apos;ve attended. Each entry
          has a QR code that gets scanned at the door. After scanning, your attendance is
          permanently recorded against the event in your passport.
        </P>
        <H2>Scanner Mode (organizers)</H2>
        <P>
          Event organizers can switch to <strong>Scanner Mode</strong> on the day of the event.
          Point your phone camera at attendee QR codes to verify their RSVP and check them in.
          The check-in count appears live in the organizer dashboard.
        </P>
        <H2>Map view</H2>
        <P>
          Many events also appear on the map at <Code>/map</Code>. Tap a pin to see details and
          RSVP without leaving the map.
        </P>
      </>
    ),
  },

  tracks: {
    title: 'Tracks & Lap Times',
    description: 'Browse racetracks, follow them, and submit lap times.',
    body: (
      <>
        <P>
          <strong>Tracks</strong> at <Code>/tracks</Code> is a directory of racetracks worldwide.
          You can browse by region (North America, Europe, Asia-Pacific, Latin America, Middle
          East &amp; Africa, Oceania) or by track type — road course, oval, drag strip, rally
          stage, or karting.
        </P>
        <H2>Following a track</H2>
        <P>
          Following a track adds it to your personal track list and includes its events in your
          notifications. It&apos;s also the simplest way to keep your lap-time submissions
          organized — your follows show up first when you log a new lap.
        </P>
        <H2>Submitting a lap time</H2>
        <P>
          On a track&apos;s page, click <strong>Submit Lap</strong>. Provide your time, the date,
          your car, the conditions (dry, damp, wet), and optionally an external GPX or video
          link. Submitted laps appear on your profile and on the track&apos;s public leaderboard.
        </P>
        <H2>Lap Compare</H2>
        <P>
          On any track page you can compare two laps side-by-side using GPX data. The map plot
          shows where each lap was faster, sector splits, and a basic delta chart. Useful for
          comparing your own progress between sessions or against another user&apos;s lap.
        </P>
        <H2>Adding a track</H2>
        <P>
          If a track isn&apos;t in the directory, you can suggest it via <strong>Add Track</strong>.
          New tracks are reviewed by the team before becoming public to avoid duplicates.
        </P>
      </>
    ),
  },

  messages: {
    title: 'Messages & Direct Chat',
    description: 'One-on-one DMs, group chats, and message etiquette.',
    body: (
      <>
        <P>
          <strong>Messages</strong> live in your <Code>/inbox</Code> — OCTNE&apos;s direct-message
          inbox. You can chat one-on-one, create group conversations, and react to messages.
          Conversations are sorted by most recent activity.
        </P>
        <H2>Starting a conversation</H2>
        <P>
          Tap <strong>New Message</strong>, then search for a username. Multiple users can be
          added to create a group chat. The first message kicks off the thread.
        </P>
        <H2>Group chats</H2>
        <P>
          Group chats can be renamed, given a custom photo, and have members added or removed by
          existing members. Leaving a group removes you from future messages but keeps your
          history visible to the others.
        </P>
        <H2>Privacy settings</H2>
        <P>
          You control who can DM you in <strong>Settings</strong> → Privacy. Choose between
          <em> everyone</em> and <em> followers only</em>. Anyone outside your filter sees your
          message button disabled.
        </P>
        <H2>Reporting and blocking</H2>
        <P>
          You can report or block any user from their profile or from inside a conversation.
          Blocked users can&apos;t message you, see your posts, or follow you. See{' '}
          <strong>Safety &amp; Reporting</strong>.
        </P>
      </>
    ),
  },

  discovery: {
    title: 'Search, Discover & Map',
    description: 'How to find users, posts, hashtags, and places.',
    body: (
      <>
        <P>
          OCTNE has three discovery surfaces: <strong>Search</strong> (intent-driven), {' '}
          <strong>Explore</strong> (algorithmic), and the <strong>Map</strong> (geographic).
        </P>
        <H2>Search</H2>
        <P>
          Tap the search bar (or visit <Code>/search</Code>) and start typing. Tabs filter results
          to <strong>For You</strong>, <strong>Users</strong>, <strong>Posts</strong>,{' '}
          <strong>Hashtags</strong>, <strong>Clubs</strong>, <strong>Shops</strong>,{' '}
          <strong>Channels</strong>, <strong>Tracks</strong>, and <strong>Rides</strong>. Recent
          searches are stored locally in your browser (the last ten) and can be cleared at any
          time.
        </P>
        <H2>Explore</H2>
        <P>
          <Code>/explore</Code> is a masonry feed of trending posts mixed with builders OCTNE
          thinks you&apos;ll want to follow. It&apos;s the place to find content from outside
          your current follow graph. Trending is recalculated frequently based on engagement
          velocity.
        </P>
        <H2>Hashtag pages</H2>
        <P>
          Every hashtag has a dedicated page at <Code>/discover?tag=hashtag</Code> showing every
          public post that uses it. Tapping a hashtag in any caption takes you there.
        </P>
        <H2>Map</H2>
        <P>
          <Code>/map</Code> plots events, car meets, tracks, shops, and cruise routes on an
          interactive map. Filter by pin type to focus on one category. Tap any pin to see
          details and RSVP without leaving the map.
        </P>
      </>
    ),
  },

  collections: {
    title: 'Collections',
    description: 'Curate cars and rides into shareable, ranked lists.',
    body: (
      <>
        <P>
          <strong>Collections</strong> are curated lists of cars (your own or other users&apos;
          public cars) that you assemble around a theme — <em>JDM Hall of Fame</em>, <em>Track
          Toys Under $50k</em>, <em>Cars I&apos;d Daily</em>. Visit any collection at{' '}
          <Code>/collections/[id]</Code>.
        </P>
        <H2>Privacy modes</H2>
        <Ul>
          <Li><strong>Public</strong> — anyone can find and view the collection.</Li>
          <Li><strong>Followers only</strong> — only your followers can view it.</Li>
          <Li><strong>Private</strong> — only you can view it.</Li>
        </Ul>
        <H2>Adding cars</H2>
        <P>
          Open a collection you own and tap <strong>Add Car</strong>. Search for the car
          you&apos;d like to add by username + car name, or paste a link to a public car page.
          You can reorder cars by dragging.
        </P>
        <H2>Sharing</H2>
        <P>
          Public collections have a shareable URL and a share dialog with copy-link and
          social-share options. Each car card in a public collection shows the year, make, model,
          color, mileage, mod count, and likes.
        </P>
      </>
    ),
  },

  // ── Marketplace & Money ──────────────────────────────────────────────
  marketplace: {
    title: 'Marketplace',
    description: 'Buy and sell vehicles and parts on OCTNE.',
    body: (
      <>
        <P>
          The <strong>Marketplace</strong> at <Code>/marketplace</Code> hosts listings for
          complete vehicles and individual parts (wheels, engines, suspension, exhaust, body kits,
          and more). You can filter by category, condition, location, price range, and saved
          searches.
        </P>
        <H2>Creating a listing</H2>
        <P>
          Tap <strong>Sell Something</strong> to open the listing dialog. Provide a title, price,
          photos, condition, description, and optionally the car make and model the part fits.
          Listings stay live until you mark them sold or delete them.
        </P>
        <H2>Conditions</H2>
        <Ul>
          <Li><strong>New</strong> — never installed, original packaging.</Li>
          <Li><strong>Like new</strong> — installed briefly or never used in anger.</Li>
          <Li><strong>Good</strong> — used, fully functional, normal wear.</Li>
          <Li><strong>Fair</strong> — used with cosmetic or minor functional issues.</Li>
          <Li><strong>Parts only</strong> — non-functional or for-parts.</Li>
        </Ul>
        <H2>Saving and price-drop alerts</H2>
        <P>
          Tap the heart on any listing to save it to <Code>/saved-marketplace</Code>. Enable
          <strong> Price-drop alerts</strong> to be notified by in-app notification and email if
          the seller lowers the price.
        </P>
        <H2>Saved searches</H2>
        <P>
          From the search bar, tap <strong>Save Search</strong> to keep an active filter set
          under <strong>My Searches</strong>. New listings matching your saved search trigger an
          alert.
        </P>
        <H2>Buying</H2>
        <P>
          OCTNE doesn&apos;t process individual marketplace transactions. Use the message thread
          on a listing to coordinate inspection, payment, and shipment with the seller. For
          dealer inventory, see <strong>Dealer Tools</strong>.
        </P>
        <Note>
          Trust your instincts. Meet in public for in-person transactions, verify titles, and
          never wire funds for a vehicle you haven&apos;t inspected. Report suspicious listings
          from the listing page menu — see <strong>Safety &amp; Reporting</strong>.
        </Note>
      </>
    ),
  },

  shop: {
    title: 'Shops & Storefronts',
    description: 'Open a Shop, list products, manage inventory and orders.',
    body: (
      <>
        <P>
          A <strong>Shop</strong> is a full storefront under your username at{' '}
          <Code>/shop/[handle]</Code>. Unlike one-off marketplace listings, a shop is a persistent
          inventory you maintain over time, with orders, customers, discounts, and analytics.
        </P>
        <H2>Opening a shop</H2>
        <P>
          From your profile, choose <strong>Open a Shop</strong>. Pick a handle (your shop URL),
          a logo, a banner, and connect your Stripe account so OCTNE can pay you out. See{' '}
          <strong>Payments &amp; Stripe</strong> for how Connect works.
        </P>
        <H2>Inventory management</H2>
        <P>
          The shop dashboard at <Code>/shop/[handle]/dashboard</Code> has tabs for{' '}
          <strong>Overview</strong>, <strong>Inventory</strong>, <strong>Orders</strong>,{' '}
          <strong>Shipping</strong>, <strong>Customers</strong>, <strong>Discounts</strong>,{' '}
          <strong>Analytics</strong>, <strong>Locations</strong>, <strong>Hours</strong>,{' '}
          <strong>Integrations</strong>, and <strong>Settings</strong>. You can list products,
          set stock levels, manage variants, and bulk-edit pricing.
        </P>
        <H2>Discounts</H2>
        <P>
          Create percentage or flat-amount discount codes from the <strong>Discounts</strong>{' '}
          tab. Codes can have minimum order values, expiration dates, and per-customer usage
          limits.
        </P>
        <H2>Wishlist and price-drop alerts</H2>
        <P>
          Customers can add your products to their wishlist. If you lower the price on a wished
          product, OCTNE notifies the customer in-app and (when enabled) sends a transactional
          email. Use this carefully — frequent discounting trains customers to wait.
        </P>
        <H2>Analytics</H2>
        <P>
          The <strong>Analytics</strong> tab shows sales trends, top products, average order
          value, and conversion metrics. For deeper creator-side insights see{' '}
          <strong>Insights &amp; Analytics</strong>.
        </P>
      </>
    ),
  },

  dealers: {
    title: 'Dealer Tools',
    description: 'Claim a dealership, manage leads, reviews, and inventory.',
    body: (
      <>
        <P>
          OCTNE has a dedicated dealer surface separate from individual sellers. A claimed
          dealership lives at <Code>/dealer/[dealerSlug]</Code> and gives you a multi-tab admin
          for inventory, leads, reviews, branding, billing, team, and a danger zone for
          destructive actions.
        </P>
        <H2>Claiming your dealership</H2>
        <P>
          If your dealership exists in OCTNE&apos;s directory, claim it from the dealership page.
          Verification typically requires confirming an email at the dealership&apos;s domain or
          a phone callback. Until verification completes, the dashboard is read-only.
        </P>
        <H2>Dashboard</H2>
        <P>
          The dashboard surfaces sales metrics, recent leads, and overdue inquiries. A lead is
          flagged as <strong>overdue</strong> if you haven&apos;t responded within four hours of
          first contact.
        </P>
        <H2>Listings</H2>
        <P>
          Manage every active and sold inventory item from <strong>Listings</strong>. Bulk import
          via CSV is on the roadmap; for now listings are added individually. Mark a listing as
          sold when the deal closes — sold listings still contribute to your dealership&apos;s
          reputation and history.
        </P>
        <H2>Leads</H2>
        <P>
          Every customer inquiry shows up in <strong>Leads</strong> as a thread. Reply directly
          from the dashboard. Lead history is preserved indefinitely.
        </P>
        <H2>Reviews</H2>
        <P>
          Customers can rate your dealership 1–5 stars and leave a written review. From the{' '}
          <strong>Reviews</strong> tab you can request a review by email, view all incoming
          reviews, filter by rating, and respond publicly to negative reviews.
        </P>
        <H2>Branding</H2>
        <P>
          Customize colors, logo, banner, business hours, and locations under{' '}
          <strong>Branding</strong>. Multi-location dealerships can list each rooftop separately.
        </P>
        <H2>Billing</H2>
        <P>
          Dealer subscription tiers (when active) are managed under <strong>Billing</strong>.
          See <strong>Payments &amp; Stripe</strong> for how the underlying billing works.
        </P>
        <H2>Danger zone</H2>
        <P>
          The <strong>Danger</strong> tab holds destructive actions — deleting the dealership,
          resetting metrics, or transferring ownership. Each requires explicit confirmation;
          some require admin approval.
        </P>
      </>
    ),
  },

  'creator-monetization': {
    title: 'Creator Monetization',
    description: 'Subscriptions, ads, payouts, and the Creator Suite.',
    body: (
      <>
        <P>
          The <strong>Creator Suite</strong> at <Code>/creator-suite</Code> is OCTNE&apos;s
          monetization hub for channel creators, builders, and shop owners. It consolidates
          subscriber management, ad revenue, verification, earnings, payouts, and the referral
          program in one dashboard.
        </P>
        <H2>Tabs in the Creator Suite</H2>
        <Ul>
          <Li><strong>Overview</strong> — top-line earnings and traffic across your content.</Li>
          <Li><strong>Subscriptions</strong> — paying subscribers, tier mix, churn, and tier configuration.</Li>
          <Li><strong>Ads</strong> — ad revenue and per-Rev / per-channel CPM trends.</Li>
          <Li><strong>Verification</strong> — your verification status and any open requests.</Li>
          <Li><strong>Earnings</strong> — gross and net revenue by source and date range.</Li>
          <Li><strong>Payouts</strong> — your Stripe Connect status, scheduled payouts, and history.</Li>
          <Li><strong>Referrals</strong> — your referral code, signups credited, and any rewards.</Li>
        </Ul>
        <H2>Eligibility</H2>
        <P>
          Monetization eligibility depends on your account type, follower count, content history,
          and policy compliance. The exact threshold varies by feature; the Creator Suite shows
          your current eligibility per feature. If you&apos;re not yet eligible, the dashboard
          explains the next step.
        </P>
        <H2>Subscriptions</H2>
        <P>
          Channel and shop owners can offer one or more paid subscription tiers. Subscribers get
          access to subscriber-only posts, channel videos, group chats, or merchandise discounts
          depending on your tier configuration. Subscribers are billed monthly or annually
          through Stripe.
        </P>
        <H2>Ad revenue</H2>
        <P>
          Eligible Revs and channel videos run pre-roll or mid-roll ads. Revenue is aggregated
          daily and surfaced under <strong>Ads</strong>. You can opt out individual videos from
          monetization at upload time.
        </P>
        <H2>Earnings and payouts</H2>
        <P>
          Earnings from subscriptions, ads, and shop sales aggregate into your{' '}
          <strong>Earnings</strong> tab. OCTNE pays out via Stripe on a rolling schedule — see{' '}
          <strong>Payments &amp; Stripe</strong> for payout cadence and Connect onboarding.
        </P>
      </>
    ),
  },

  payments: {
    title: 'Payments & Stripe',
    description: 'How OCTNE processes payments, payouts, and refunds.',
    body: (
      <>
        <P>
          OCTNE uses <strong>Stripe</strong> for all payments — paid event tickets, paid
          subscriptions, shop purchases, dealer billing, and creator payouts. You don&apos;t need
          a Stripe account to <em>buy</em> on OCTNE; you do need one to <em>sell</em> or get
          paid.
        </P>
        <H2>Stripe Connect (sellers and creators)</H2>
        <P>
          Sellers, shop owners, dealers, and monetizing creators connect a{' '}
          <strong>Stripe Express</strong> account to OCTNE. The onboarding flow asks for
          identity, tax, and bank details — Stripe collects this directly so OCTNE never sees
          your sensitive financial information. You can manage your Connect account from your
          Creator Suite or shop dashboard.
        </P>
        <H2>Buying</H2>
        <P>
          Checkout uses Stripe&apos;s hosted payment sheet. Major cards, Apple Pay, Google Pay,
          and (by region) Klarna or Affirm are supported. Your card details never touch
          OCTNE&apos;s servers.
        </P>
        <H2>Payout schedule</H2>
        <P>
          By default, sales are paid out daily on a rolling 7-day schedule. New Connect accounts
          may be held longer until Stripe completes its verification. Payouts arrive in the bank
          account on file with Stripe — typically next-business-day in the US, 1–3 days in
          Europe.
        </P>
        <H2>Refunds</H2>
        <P>
          Sellers can issue refunds (full or partial) from the order page in the shop dashboard.
          Refunded amounts are subtracted from the next payout; if the payout has already left,
          the amount is debited and may show as a negative balance until covered by future
          sales.
        </P>
        <H2>Disputes and chargebacks</H2>
        <P>
          If a buyer initiates a chargeback through their bank, Stripe notifies you and OCTNE.
          You have a fixed window (usually 7–10 days) to upload evidence — order details, proof
          of delivery, communications with the buyer. OCTNE&apos;s support team can guide you
          through the response.
        </P>
        <H2>Taxes</H2>
        <P>
          Stripe Tax is automatically applied to applicable transactions where it&apos;s enabled.
          You&apos;re responsible for ensuring you&apos;ve set the right tax categories on your
          products and that your Stripe account is configured for the regions you sell in.
          OCTNE doesn&apos;t provide tax advice — consult an accountant.
        </P>
        <Note>
          OCTNE charges a platform fee on monetized transactions. The exact rate depends on the
          transaction type (subscriptions, marketplace, ad rev share). Your Creator Suite shows
          gross and net per transaction so you always see the fee deducted.
        </Note>
      </>
    ),
  },

  // ── Account & Tools ──────────────────────────────────────────────────
  notifications: {
    title: 'Notifications & Inbox',
    description: 'Notification types, your activity feed, and how to stay focused.',
    body: (
      <>
        <P>
          Your <strong>Inbox</strong> at <Code>/inbox</Code> is OCTNE&apos;s consolidated
          notification feed. It&apos;s grouped into <strong>Today</strong>,{' '}
          <strong>This Week</strong>, and <strong>Earlier</strong>. Activity polls every 30
          seconds while you&apos;re on the page and badges the bell icon on the top bar when
          there&apos;s something new.
        </P>
        <H2>Notification types</H2>
        <Ul>
          <Li><strong>Like</strong> — someone liked your post.</Li>
          <Li><strong>Comment</strong> — someone commented on your post.</Li>
          <Li><strong>Reply</strong> — someone replied to your comment.</Li>
          <Li><strong>Save</strong> — someone saved your post.</Li>
          <Li><strong>Follow</strong> — someone followed you.</Li>
          <Li><strong>Tag</strong> — someone mentioned you with @username.</Li>
          <Li><strong>Message</strong> — you have a new DM.</Li>
          <Li><strong>Channel video</strong> / <strong>Premiere</strong> — a channel you follow uploaded or scheduled a premiere.</Li>
          <Li><strong>Club post</strong> / <strong>Club invite</strong> — activity in clubs you&apos;re in.</Li>
          <Li><strong>Event RSVP</strong> — RSVPs to events you organize.</Li>
          <Li><strong>Marketplace offer</strong> — someone offered on a listing of yours.</Li>
          <Li><strong>Shop item</strong> — restocks or new items from shops you follow.</Li>
          <Li><strong>Collection update</strong> — a collection you follow added a car.</Li>
        </Ul>
        <H2>Mark all read / clear all</H2>
        <P>
          The header bar of the inbox has icons to <strong>mark all as read</strong> and{' '}
          <strong>clear all</strong>. Clearing is permanent.
        </P>
        <H2>Email notifications</H2>
        <P>
          You can opt into transactional email for important notifications under{' '}
          <strong>Settings</strong> → Notifications. Marketing email is opt-out by default.
        </P>
      </>
    ),
  },

  leaderboard: {
    title: 'Leaderboard',
    description: 'How OCTNE ranks builders and how you can climb.',
    body: (
      <>
        <P>
          The <strong>Leaderboard</strong> at <Code>/leaderboard</Code> ranks the top 50 builders
          by an engagement score. Trophy badges are awarded to the top three. The list updates
          continuously.
        </P>
        <H2>How the score is calculated</H2>
        <P>
          The leaderboard score is a weighted combination of:
        </P>
        <Ul>
          <Li>Total likes received on your posts (weight: 2)</Li>
          <Li>Total post count (weight: 3)</Li>
          <Li>Average build progress across cars in your garage</Li>
        </Ul>
        <P>
          The result is normalized so longtime users don&apos;t lock out new builders entirely.
          We tune weights periodically — your relative rank is more meaningful than the raw
          number.
        </P>
        <H2>Climbing</H2>
        <P>
          Posting consistently with linked cars and updated build stages tends to outperform
          one-off posts. The leaderboard rewards depth (a documented build) over reach.
        </P>
      </>
    ),
  },

  livestreams: {
    title: 'Livestreams',
    description: 'Watch and host live broadcasts on OCTNE.',
    body: (
      <>
        <P>
          OCTNE supports live broadcasts at <Code>/live/[streamId]</Code>. Live streams are
          created by eligible creators (channel owners, dealerships, shops). Streams have a live
          badge while broadcasting and switch to a replay state when they end.
        </P>
        <H2>Watching</H2>
        <P>
          Open any live URL to join the stream. The viewer count updates every ten seconds.
          Follow or subscribe to the broadcaster from the stream page to be notified of future
          broadcasts.
        </P>
        <H2>Going live</H2>
        <P>
          Eligible creators see a <strong>Go Live</strong> button on their profile. The stream
          composer collects a title, optional description, and a thumbnail. Live streams are
          recorded automatically; the replay is added to your channel after the broadcast ends.
        </P>
        <H2>Subscriber-only streams</H2>
        <P>
          Creators with active monetization can lock streams to paying subscribers. Anyone else
          who opens the URL sees a paywall with a one-tap subscribe option.
        </P>
      </>
    ),
  },

  settings: {
    title: 'Settings & Privacy',
    description: 'Profile, privacy, notifications, billing, theme, and account deletion.',
    body: (
      <>
        <P>
          The <strong>Settings</strong> page at <Code>/settings</Code> is where you control your
          account. Each section is collapsible — start with what you need.
        </P>
        <H2>Profile</H2>
        <P>
          Update your display name, username, bio, location, website, and contact email. Your
          avatar and banner are also edited here. Note that changing your username changes the URL
          of your profile.
        </P>
        <H2>Privacy</H2>
        <Ul>
          <Li><strong>Profile visibility</strong> — public or followers-only.</Li>
          <Li><strong>Show activity</strong> — whether other users can see when you&apos;re online.</Li>
          <Li><strong>Allow messages</strong> — everyone or followers only.</Li>
          <Li><strong>Allow comments on posts</strong> — everyone, followers only, or off.</Li>
          <Li><strong>Allow tags</strong> — anyone, followers only, or off.</Li>
        </Ul>
        <H2>Media preferences</H2>
        <P>
          Choose your preferred image quality and video quality, toggle auto-play on Revs and the
          feed, and enable Data Saver to reduce bandwidth use on cellular.
        </P>
        <H2>Notifications</H2>
        <P>
          Granular controls for in-app and email notifications. Mute everything or pick exactly
          which categories should reach you.
        </P>
        <H2>Theme and language</H2>
        <P>
          Toggle between light and dark mode (dark is the default). Pick your preferred language
          from the language selector — translations cover English, Spanish, French, German,
          Chinese, Japanese, Portuguese, Russian, and Arabic.
        </P>
        <H2>Billing</H2>
        <P>
          Active subscriptions and saved payment methods live under <strong>Billing</strong>.
          Cancellation is one click; refunds for paid subscriptions follow the policy in our{' '}
          Terms.
        </P>
        <H2>Data download</H2>
        <P>
          Request a complete archive of your account data (posts, comments, messages, garage,
          settings) from <strong>Privacy</strong> → Data Download. We email you the archive
          within 48 hours of the request.
        </P>
        <H2>Deleting your account</H2>
        <P>
          Account deletion is irreversible. Once confirmed, your posts, profile, garage, and
          messages are removed from OCTNE. Some anonymized aggregate data (engagement counts on
          others&apos; posts) may persist. Active subscriptions are canceled at the end of the
          current billing period.
        </P>
      </>
    ),
  },

  safety: {
    title: 'Safety, Reporting & Moderation',
    description: 'Block, mute, report, and how OCTNE moderates content.',
    body: (
      <>
        <P>
          OCTNE expects users to be respectful. If someone crosses the line, you have multiple
          tools to remove them from your experience or escalate to the moderation team.
        </P>
        <H2>Block</H2>
        <P>
          Blocking is a hard wall. A blocked user can&apos;t see your profile, posts, stories, or
          DMs, and they can&apos;t follow or message you. They aren&apos;t notified that
          they&apos;ve been blocked.
        </P>
        <H2>Mute</H2>
        <P>
          Muting a user keeps your relationship intact (they don&apos;t know) but hides their
          posts from your feed and their DMs from your inbox preview. Useful when you don&apos;t
          want a hard break.
        </P>
        <H2>Reporting</H2>
        <P>
          Tap the <strong>Report</strong> action on a post, comment, message, listing, or
          profile. Pick a reason — spam, harassment, hate speech, sexual content, illegal sales,
          intellectual property, impersonation, or other — and add an optional note. Reports go
          to OCTNE&apos;s moderation queue.
        </P>
        <H2>What we moderate</H2>
        <P>
          OCTNE removes content that violates our Terms — including illegal activity, threats,
          hate speech, sexually explicit content involving minors, deepfakes, and fraud.
          Repeated violations result in account suspension or removal.
        </P>
        <H2>Image moderation</H2>
        <P>
          Uploaded images are screened by an automated check before publishing. Anything flagged
          is rejected with a notice. The check focuses on graphic, sexual, or violent content;
          benign images occasionally false-positive — try a re-upload or a different framing.
        </P>
        <H2>Appeals</H2>
        <P>
          If a moderation action removed your content or restricted your account and you believe
          it was a mistake, email <Code>support@octne.com</Code> with the URL or a screenshot.
          Appeals are reviewed by a human.
        </P>
      </>
    ),
  },

  // ── About OCTNE ──────────────────────────────────────────────────────
  contact: {
    title: 'Contact & Support',
    description: 'How to reach OCTNE for support, legal, and press inquiries.',
    body: (
      <>
        <P>
          The fastest way to reach OCTNE for product questions, bugs, refunds, or moderation
          appeals is <Code>support@octne.com</Code>. Our team replies within two business days
          for general inquiries.
        </P>
        <H2>By topic</H2>
        <Ul>
          <Li>
            <strong>Product help, bugs, account access, refunds</strong> —{' '}
            <Code>support@octne.com</Code>
          </Li>
          <Li>
            <strong>Privacy, copyright, trademark, terms, takedowns</strong> —{' '}
            <Code>legal@octne.com</Code>
          </Li>
          <Li><strong>Press &amp; partnerships</strong> — <Code>press@octne.com</Code></Li>
        </Ul>
        <H2>What to include</H2>
        <P>
          For the fastest response, include your username, the URL of the post or page in
          question, what you expected to happen, and what actually happened. Screenshots help.
          For account access issues, send from the email address on your OCTNE account so we can
          verify it&apos;s you.
        </P>
        <H2>Status and outages</H2>
        <P>
          If something seems broken across the platform, check OCTNE&apos;s social channels for
          status updates before opening a ticket. Outages affecting many users are usually
          announced there first.
        </P>
        <H2>Security disclosures</H2>
        <P>
          If you&apos;ve identified a security vulnerability, please email{' '}
          <Code>security@octne.com</Code> with details and a proof of concept. Don&apos;t exploit
          the vulnerability beyond what&apos;s necessary to demonstrate it. We acknowledge
          credible reports within 72 hours.
        </P>
      </>
    ),
  },

  legal: {
    title: 'Legal',
    description: 'Links to our Terms, Privacy Policy, and Returns Policy.',
    body: (
      <>
        <P>
          OCTNE&apos;s legal documents are public and apply to anyone using the platform. Read
          them before signing up, and again any time we update them — we send a notice when a
          material change ships.
        </P>
        <H2>Terms of Use</H2>
        <P>
          The <strong>Terms of Use</strong> at <Code>/terms</Code> govern your use of OCTNE — the
          rules of the road, what&apos;s prohibited, account suspension policies, and the
          governing law.
        </P>
        <H2>Privacy Policy</H2>
        <P>
          The <strong>Privacy Policy</strong> at <Code>/privacy</Code> explains what data we
          collect, why, who we share it with, how long we keep it, and the rights you have over
          your data (access, correction, deletion, portability).
        </P>
        <H2>Returns Policy</H2>
        <P>
          The <strong>Returns Policy</strong> at <Code>/returns</Code> covers shop purchases — the
          window to return or exchange, who pays for return shipping, and how refunds are issued.
          Marketplace and dealer transactions are outside this policy and follow the seller&apos;s
          posted terms.
        </P>
        <H2>Reporting copyright or trademark issues</H2>
        <P>
          To report content that infringes your copyright or trademark, email{' '}
          <Code>legal@octne.com</Code> with proof of ownership and the specific URL(s) at issue.
          We follow a notice-and-takedown process consistent with applicable law.
        </P>
      </>
    ),
  },
};

// ── Helpers ──────────────────────────────────────────────────────────────

export function findTopic(slug) {
  return TOPICS[slug] ?? null;
}

export function getOrderedSlugs() {
  return CATEGORIES.flatMap((c) => c.topics);
}

export function getNeighbors(slug) {
  const ordered = getOrderedSlugs();
  const idx = ordered.indexOf(slug);
  if (idx === -1) return { prev: null, next: null };
  const prevSlug = idx > 0 ? ordered[idx - 1] : null;
  const nextSlug = idx < ordered.length - 1 ? ordered[idx + 1] : null;
  return {
    prev: prevSlug ? { slug: prevSlug, title: TOPICS[prevSlug].title } : null,
    next: nextSlug ? { slug: nextSlug, title: TOPICS[nextSlug].title } : null,
  };
}

export function findCategoryFor(slug) {
  return CATEGORIES.find((c) => c.topics.includes(slug)) ?? null;
}
